// components/steps/MedicationDetailsStep.jsx
import React, { useState, useEffect } from 'react';
import { Input, Button, Card, AutoComplete, message, Tag, Modal, Form, InputNumber, Select, Row, Col } from 'antd';
import { SearchOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { post, get } from '../../../services/ApiService';

const { Option } = Select;

const MedicationDetails = ({ medications, setMedications }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showManualInput, setShowManualInput] = useState(false);
  const [isManualModalVisible, setIsManualModalVisible] = useState(false);
  const [form] = Form.useForm();

  // Search for drugs based on query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setSearchResults([]);
      return;
    }

    fetchDrugs(searchQuery);
  }, [searchQuery]);

  const fetchDrugs = async (query) => {
    try {
      const response = await get(`api/drugs?search=${query}`);
      if (response.success) {
        const data = response.data;
        if (Array.isArray(data) && data.length > 0) {
          setSearchResults(response.data);
          setShowManualInput(false);
        } else {
          setSearchResults([]);
          setShowManualInput(true);
        }
      } else {
        setSearchResults([]);
        setShowManualInput(true);
      }
    } catch (error) {
      console.error('Error fetching drugs:', error);
      setSearchResults([]);
    }
  }

  // Handle drug selection from search
  const handleDrugSelect = (value, option) => {
    const drug = option.drug;

    // Check if drug is already selected
    if (medications.some(med => med.drugId === drug._id)) {
      message.warning('This drug is already added to your list');
      return;
    }

    // Create display name
    const displayName = `${drug.name} ${drug.strength} ${drug.formulation} x${drug.quantity}`;

    // Add to medications state with unified structure
    setMedications(prev => [...prev, {
      drugId: drug._id,
      name: displayName,
      drugDetails: null
    }]);

    setSearchQuery('');
    setSearchResults([]);
    setShowManualInput(false);
  };

  // Handle manual form submission
  const handleManualSubmit = async (values) => {
    try {
      // Create a formatted name for display
      const displayName = `${values.name} ${values.strength} ${values.formulation} x${values.quantity}`;

      // Check if similar drug is already selected
      const isDuplicate = medications.some(med =>
        med.name === displayName ||
        (med.drugDetails &&
          med.drugDetails.name === values.name &&
          med.drugDetails.strength === values.strength &&
          med.drugDetails.formulation === values.formulation)
      );

      if (isDuplicate) {
        message.warning('This drug is already added to your list');
        return;
      }

      // Add to medications state
      setMedications(prev => [...prev, {
        drugId: null,
        name: displayName,
        drugDetails: {
          name: values.name,
          strength: values.strength,
          formulation: values.formulation,
          quantity: values.quantity
        }
      }]);

      // Reset form and close modal
      form.resetFields();
      setIsManualModalVisible(false);
      setSearchQuery('');
      setShowManualInput(false);

      message.success('Medication added successfully');
    } catch (error) {
      console.error('Error adding manual drug:', error);
      message.error('Failed to add medication');
    }
  };

  // Remove a medication
  const removeMedication = (index) => {
    const newMedications = [...medications];
    newMedications.splice(index, 1);
    setMedications(newMedications);
    message.success('Medication removed');
  };

  // Format medication display name
  const formatDisplayName = (medication) => {
    if (medication.name) return medication.name;

    // Fallback to constructing from drugDetails
    if (medication.drugDetails) {
      const { drugDetails } = medication;
      return `${drugDetails.name} ${drugDetails.strength} ${drugDetails.formulation} x${drugDetails.quantity}`;
    }

    return 'Unknown Medication';
  };

  return (
    <div className="step-content step_content_add">
      <label className="mb-3" style={{fontSize:"15px"}}>Search for your prescribed medications or enter them manually if not listed.</label>

      {/* Search Section */}
      <div className="medication-search-section mb-4">
        <div className="search-input-wrapper">
<AutoComplete
  value={searchQuery}
  onChange={setSearchQuery}
  options={
    searchQuery.trim() !== '' // Only when user typed something
      ? [
          // Map existing search results
          ...searchResults.map(drug => ({
            value: drug.name,
            label: (
              <div className="drug-option">
                <div className="drug-name">{drug.name} {drug.strength} {drug.formulation} x{drug.quantity}</div>
              </div>
            ),
            drug: drug
          })),
          // Add manual entry option at the end
          {
            value: '__manual__',
            label: (
              <div
                className="drug-option manual-entry-option"
                style={{ fontWeight: 'bold', color: '#1890ff', cursor: 'pointer' }}
              >
                + Add Medication Manually
              </div>
            ),
            drug: null
          }
        ]
      : [] // Empty array if no search query
  }
  onSelect={(value, option) => {
    if (value === '__manual__') {
      setIsManualModalVisible(true);
      setSearchQuery('');
      return;
    }
    handleDrugSelect(value, option);
  }}
  placeholder="Search for medications..."
  style={{ width: '100%' }}
  filterOption={false}
/>


        </div>

        {/* Manual Input Option */}
        {showManualInput && (
          <div className="manual-input-section mt-3 p-3" style={{ border: '1px solid #d9d9d9', borderRadius: '6px' }}>
            <p className="mb-2">No medications found for "{searchQuery}". Add it manually:</p>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsManualModalVisible(true)}
              style={{ borderRadius: '6px' }}
            >
              Add Medication Manually
            </Button>
          </div>
        )}
      </div>

      {/* Selected Medications */}
      <div className="selected-medications mb-4" style={{ border: '1px dashed #d9d9d9', padding: '15px', borderRadius: '6px' }}>
        {medications.length > 0 ? (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">Selected Medications</h6>
              <span className="text-muted small">{medications.length} medication(s)</span>
            </div>
            <hr />
            {medications.map((med, index) => (
              <div key={index} className="d-flex justify-content-between align-items-center mb-2 p-2" style={{
                borderBottom: '1px solid #f0f0f0',
              }}>
                <div className="d-flex align-items-center">
                  <span className="me-2 text-muted" style={{ fontWeight: 'bold', minWidth: '20px' }}>{index + 1}.</span>
                  <div>
                    <p className="drug-name-text mb-0">
                      {formatDisplayName(med)}
                      {med.drugId === null && (
                        <Tag color="orange" style={{ marginLeft: '8px', fontSize: '10px' }}>
                          Manual Entry
                        </Tag>
                      )}
                    </p>
                  </div>
                </div>
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  onClick={() => removeMedication(index)}
                  size="small"
                  danger
                  style={{ borderRadius: '50%', background: "#E2B9B3" }}
                  title="Remove medication"
                />
              </div>
            ))}
          </>
        ) : (
          <div className="empty-state text-center p-4" style={{ border: '1px dashed #d9d9d9', borderRadius: '6px' }}>
            <SearchOutlined style={{ fontSize: '24px', color: '#bfbfbf', marginBottom: '8px' }} />
            <p className="text-muted mb-0">Search for medications above to add them to your list</p>
          </div>
        )}
      </div>

      {/* Manual Drug Entry Modal */}
      <div className='transcribing-wrapper'>
        <Modal
          title="Add Medication"
          open={isManualModalVisible}
          onCancel={() => {
            form.resetFields();
            setIsManualModalVisible(false);
          }}
          footer={null}
          width={600}
          style={{ zIndex: 1000 }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleManualSubmit}
            initialValues={{
              formulation: 'tablets',
              quantity: 1
            }}
          >
            <Form.Item
              name="name"
              label="Drug Name"
              rules={[{ required: true, message: 'Please enter the drug name' }]}
            >
              <Input placeholder="e.g., Amoxicillin" />
            </Form.Item>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  name="strength"
                  label="Strength"
                  rules={[{ required: true, message: 'Please enter strength' }]}
                >
                  <Input placeholder="e.g., 500mg" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="formulation"
                  label="Formulation"
                  rules={[{ required: true, message: 'Please select formulation' }]}
                >
                  <Select>
                    <Option value="tablets">Tablets</Option>
                    <Option value="capsules">Capsules</Option>
                    <Option value="injection">Injection</Option>
                    <Option value="solution">Solution</Option>
                    <Option value="cream">Cream</Option>
                    <Option value="ointment">Ointment</Option>
                    <Option value="drops">Drops</Option>
                    <Option value="inhaler">Inhaler</Option>
                    <Option value="spray">Spray</Option>
                    <Option value="gel">Gel</Option>
                    <Option value="powder">Powder</Option>
                    <Option value="other">Other</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="quantity"
                  label="Quantity"
                  rules={[{ required: true, message: 'Please enter quantity' }]}
                >
                  <InputNumber
                    min={1}
                    placeholder="e.g., 21"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Button type="primary" htmlType="submit" style={{ marginRight: '8px' }}>
                Add Medication
              </Button>
              <Button onClick={() => {
                form.resetFields();
                setIsManualModalVisible(false);
              }}>
                Cancel
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default MedicationDetails;